'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { $addonAutomationPrice, loadAddonAutomationPriceEvent } from '@nx-frontend/shared/store/addon-automation-price';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { GetAutomationPopupPayload, PopupType } from '@nx-frontend/shared/types';
import { OpenModalParams } from '@nx-frontend/shared/types';
import { mapPlanToSubscription } from '@nx-frontend/shared/utils';
import { getOpenPurchaseAutomationBillingPopup } from './purchase-automation-billing-popup';

export interface GetAutomationPopupProps {
  openModal: (params: OpenModalParams) => void;
  payload?: GetAutomationPopupPayload;
}

export const getOpenAutomationPopup = ({ openModal, payload }: GetAutomationPopupProps) => {
  openModal({
    content: PopupType.GET_AUTOMATION,
    showCloseButton: false,
    modalContainerStyles: 'bg-[#002730]',
    payload,
  });
};

const automationPrice = {
  monthly: 10,
  yearly: 7,
};

export function GetAutomationPopup() {
  const [currentOrganization, billingInfo, addonAutomationPrice, openModal, closeModal, loadAddonAutomationPrice] =
    useUnit([
      $currentOrganization,
      $billingInfo,
      $addonAutomationPrice,
      openModalEvent,
      closeModalEvent,
      loadAddonAutomationPriceEvent,
    ]);
  const subscriptionPeriod = mapPlanToSubscription(billingInfo).period as keyof typeof automationPrice;

  useEffect(() => {
    if (!addonAutomationPrice.isLoaded && billingInfo.subscriptionId) {
      // lazy load addon Automation prices
      loadAddonAutomationPrice({
        orgId: currentOrganization.globalId,
        subscriptionId: billingInfo.subscriptionId,
        quantity: 1,
      });
    }
  }, [currentOrganization, billingInfo, addonAutomationPrice.isLoaded, loadAddonAutomationPrice]);

  const confirmClick = useCallback(async () => {
    if (addonAutomationPrice.isLoaded) {
      // waiting until Automation limit popup close then open Automation billing confirm popup
      setTimeout(() => {
        getOpenPurchaseAutomationBillingPopup({
          openModal,
          payload: {
            payloadType: PopupType.PURCHASE_AUTOMATION_BILLING,
          },
        });
      }, 250);
      closeModal();
    }
  }, [openModal, closeModal, addonAutomationPrice.isLoaded]);

  const onQuantityChange = useCallback(
    (value: number) => {
      if (billingInfo.subscriptionId) {
        loadAddonAutomationPrice({
          orgId: currentOrganization.globalId,
          subscriptionId: billingInfo.subscriptionId,
          quantity: value,
        });
      }
    },
    [currentOrganization, billingInfo, loadAddonAutomationPrice]
  );

  return (
    <div className="flex flex-col text-center pl-[11px] pr-[11px]">
      <div className="flex flex-col ml-[32px] mr-[32px] mt-[47px] mb-[47px]">
        <div
          className="cursor-pointer text-gray-400 hover:text-gray-700 absolute top-[24px] right-[24px]"
          style={{ width: 18, height: 18 }}
          onClick={closeModal}
        >
          <Icon icon={IconsMap.CLOSE} />
        </div>
        <div className="inline-flex flex-col items-center gap-4 text-white text-2xl font-bold leading-tight">
          Unlock more automation runs
        </div>
        <div
          className="flex mt-[32px] flex-row p-8 items-start gap-[48px] rounded-[8px] bg-white w-[648px]"
          style={{ boxShadow: '0px 0px 70px 0px rgba(22, 116, 146, 0.10)' }}
        >
          <div className="flex flex-col w-[268px]">
            <h2 className="font-semibold text-[18px] mb-[8px] mt-[8px] text-left">Get Automation Add-on</h2>
            <div className="flex items-center gap-[8px] mb-[8px]">
              <div className="text-[#176B87] text-[34px]">${automationPrice[subscriptionPeriod]}</div>
              <div className="text-[12px] w-[86px] text-left" style={{ color: 'rgba(0, 0, 0, 0.30)' }}>
                per add-on per month
              </div>
            </div>
            <div className="relative w-full mb-3">
              <AutomationPopupSelect
                onChange={onQuantityChange}
                options={Array(5)
                  .fill('')
                  .map((_, index) => {
                    const i = index + 1;
                    return {
                      value: i,
                      label: `${i} add-on${1 < i ? `'s` : ''} (${1000 * i} runs)`,
                    };
                  })}
              />
            </div>
            <div>
              <Button
                type={ButtonType.FLAT}
                onClick={confirmClick}
                bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
                radius="rounded"
                data-cy={
                  'unlock-automation-get-addon-btn-' +
                  (addonAutomationPrice.isLoaded ? 'price-loaded' : 'price-not-loaded')
                }
                className="m-h-[36px] h-[36px] w-[192px]"
              >
                {!addonAutomationPrice.isLoaded ? (
                  <Icon icon={IconsMap.LOADER} className="animate-spin" />
                ) : (
                  <span style={{ fontFamily: 'Open Sans', fontSize: '13px' }}>Get add-on</span>
                )}
              </Button>
            </div>
          </div>
          <div className="flex flex-col">
            <img src="/assets/img/automation-add-on.png" alt="" width="268" height="198" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AutomationPopupSelect({
  onChange,
  options,
}: {
  onChange: (value: number) => void;
  options: { value: number; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(options[0]);
  const domRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onContainerMouseDown = (event: MouseEvent) => {
      if (event.target === domRef.current || domRef.current?.contains(event.target as Node | null)) {
        return;
      }
      setOpen(false);
    };

    if (open) {
      const container = document.getElementById('ModalContainer');
      if (container) {
        container.addEventListener('mousedown', onContainerMouseDown);
        return () => {
          container.removeEventListener('mousedown', onContainerMouseDown);
        };
      }
    }
  }, [open]);

  const onSelectClick = () => {
    setOpen(!open);
  };

  const onSelectChange = ({ value, label }: { value: number; label: string }) => {
    onChange(value);
    setCurrent({ value, label });
    setOpen(false);
  };

  return (
    <div className="fc-header-select relative z-10 select-none" ref={domRef}>
      <div
        className={classNames(
          'px-2.5 py-1 rounded border border-neutral-200 cursor-pointer flex items-center justify-between',
          {
            'hover:bg-neutral-200': !open,
          }
        )}
        onClick={onSelectClick}
      >
        <span className="fc-header-select-current block whitespace-nowrap">{current.label}</span>
        <Icon icon={IconsMap.ARROW_DOWN} className="w-4 text-neutral-500 flex-none" />
      </div>
      <div
        className={classNames('w-full absolute top-full left-0 rounded border border-neutral-200 bg-white text-left', {
          hidden: !open,
        })}
      >
        {options.map((option) => (
          <div
            className="px-2.5 py-1 hover:bg-neutral-200 cursor-pointer"
            onClick={() => onSelectChange(option)}
            key={option.value + '-automation-value'}
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
  );
}
